import { parseDay } from '@map-my-money/engine';
import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router';
import { CASHFLOW_LEGEND, CashflowChart } from '../components/charts/CashflowChart.tsx';
import { ChatBanner, ResultStamp, useChatLink } from '../components/ChatLink.tsx';
import { ForecastChart, forecastChartLabel } from '../components/charts/ForecastChart.tsx';
import { ALL_SERIES, SeriesChips, TimeframePicker, toggleSeries } from '../components/forecastControls.tsx';
import { AskAssistant } from '../components/Assistant.tsx';
import { GoalStatus } from '../components/cards.tsx';
import { Icon } from '../components/Icon.tsx';
import { PageHeader } from '../components/Shell.tsx';
import { Delta, Ring, Segmented } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { compactMoney, money, monthYear, percent } from '../lib/format.ts';
import { useFlowYears, useForecastResult, useForecastView, useIsDesktop, useLocalPreference, type FlowYear } from '../lib/hooks.ts';
import { forecastResult, num, type ForecastView, type SeriesKey } from '../lib/model.ts';
import type { ForecastResult } from '@map-my-money/views';
import { useStore } from '../state/store.tsx';
import { useDocumentTitle } from '../lib/documentTitle.ts';

type Mode = 'cumulative' | 'cashflow';

export function ForecastScreen() {
  useDocumentTitle(t('nav.forecast'));
  const { plan, waypoints } = useStore();
  const desktop = useIsDesktop();
  const link = useChatLink();
  const [years, setYears] = useLocalPreference('mm.forecast.years', link.years ?? plan?.configuration.timeframeYears ?? 10);
  useEffect(() => {
    if (link.years !== null) setYears(link.years);
  }, [link.years, setYears]);
  const [mode, setMode] = useState<Mode>('cumulative');
  const [visible, setVisible] = useState<ReadonlySet<SeriesKey>>(ALL_SERIES);
  const view = useForecastView(years);
  const flows = useFlowYears(view);
  const result = useForecastResult(years);
  const linkYears = link.years ?? plan?.configuration.timeframeYears ?? years;
  const resultAt = useCallback(
    (asOf: string) => (plan ? forecastResult(plan, waypoints, parseDay(asOf), { years: linkYears, resolution: link.resolution, maxPoints: link.maxPoints }) : null),
    [plan, waypoints, linkYears, link.resolution, link.maxPoints],
  );
  if (!view || !result) return null;
  const props = { view, flows, result, resultAt, years, setYears, mode, setMode, visible, onToggle: (k: SeriesKey) => setVisible((v) => toggleSeries(v, k)) };
  return desktop ? <DesktopForecast {...props} /> : <MobileForecast {...props} />;
}

interface Props {
  view: ForecastView;
  flows: readonly FlowYear[];
  result: ForecastResult;
  resultAt: (asOf: string) => ForecastResult | null;
  years: number;
  setYears: (y: number) => void;
  mode: Mode;
  setMode: (m: Mode) => void;
  visible: ReadonlySet<SeriesKey>;
  onToggle: (k: SeriesKey) => void;
}

function useHeadline(view: ForecastView) {
  const change = view.finalNetWorth - view.current.netWorth;
  const pct = view.current.netWorth !== 0 ? (change / Math.abs(view.current.netWorth)) * 100 : 0;
  return { change, pct };
}

function Chart({ view, flows, mode, visible, height, compact }: { view: ForecastView; flows: readonly FlowYear[]; mode: Mode; visible: ReadonlySet<SeriesKey>; height: number; compact?: boolean }) {
  if (view.rows.length === 0) return null;
  if (mode === 'cashflow') return <CashflowChart years={flows} height={height} compact={compact ?? false} />;
  return (
    <ForecastChart
      rows={view.rows}
      visible={visible}
      milestones={view.milestones}
      height={height}
      compact={compact ?? false}
      label={forecastChartLabel(view.rows)}
      now={view.current.netWorth}
    />
  );
}

function ModeToggle({ mode, setMode }: { mode: Mode; setMode: (m: Mode) => void }) {
  return (
    <Segmented
      label={t('forecast.mode')}
      variant="pill"
      options={[{ value: 'cumulative', label: t('forecast.cumulative') }, { value: 'cashflow', label: t('forecast.cashflow') }]}
      value={mode}
      onChange={setMode}
    />
  );
}

function CashflowLegend() {
  return (
    <ul className="legend">
      {CASHFLOW_LEGEND.map((l) => (
        <li key={l.color}>
          <span className="legend__swatch" style={{ background: l.color }} aria-hidden="true" />
          {l.label()}
        </li>
      ))}
    </ul>
  );
}

function DesktopForecast({ view, flows, result, resultAt, years, setYears, mode, setMode, visible, onToggle }: Props) {
  const { change, pct } = useHeadline(view);
  const heroId = useId();
  return (
    <div className="page forecast-desktop">
      <PageHeader
        eyebrow={t('goals.eyebrow')}
        title={t('forecast.title')}
        actions={
          <Link to="/what-if" className="button button--outline-mint">
            <Icon name="whatIf" size={16} />
            {t('forecast.whatIf')}
          </Link>
        }
      />
      <ChatBanner recompute={resultAt} />
      <div className="forecast-desktop__grid">
        <section className="card card--bento forecast-card" aria-labelledby={heroId}>
          <div className="forecast-card__top">
            <div className="hero-stat">
              <h2 className="eyebrow" id={heroId}>{t('forecast.projectedNetWorth')}</h2>
              <span className="hero-amount hero-amount--xl">{money(view.finalNetWorth)}</span>
              <span className="hero-growth">
                <Delta value={change}>{compactMoney(change, { sign: true })} ({percent(pct, { sign: true })})</Delta>
                <span className="muted"> {t('forecast.from', { amount: compactMoney(view.current.netWorth) })}</span>
              </span>
            </div>
            <div className="forecast-card__switches">
              <TimeframePicker value={years} onChange={setYears} />
              <ModeToggle mode={mode} setMode={setMode} />
            </div>
          </div>
          {mode === 'cumulative' ? <SeriesChips visible={visible} onToggle={onToggle} /> : <CashflowLegend />}
          <Chart view={view} flows={flows} mode={mode} visible={visible} height={440} />
        </section>
        <div className="stack-lg">
          <GoalsPanel view={view} />
          <KeyNumbers view={view} result={result} />
        </div>
      </div>
      <AskAssistant view="forecast" />
      <ResultStamp result={result} />
    </div>
  );
}

function MobileForecast({ view, flows, result, resultAt, years, setYears, mode, setMode, visible, onToggle }: Props) {
  const { change, pct } = useHeadline(view);
  return (
    <div className="page page--mobile">
      <header className="mobile-header">
        <h1 className="mobile-title">{t('forecast.title')}</h1>
        <Link to="/what-if" className="button button--sm button--outline-mint">
          <Icon name="whatIf" size={14} />
          {t('forecast.whatIf')}
        </Link>
      </header>
      <ChatBanner recompute={resultAt} />
      <section className="card card--pad-lg forecast-summary" aria-label={t('forecast.projectedNetWorth')}>
        <span className="eyebrow">{t('forecast.projectedNetWorth')}</span>
        <span className="hero-amount hero-amount--md">{money(view.finalNetWorth)}</span>
        <TimeframePicker value={years} onChange={setYears} size="sm" />
        <span className="hero-growth hero-growth--center">
          <Delta value={change}>{compactMoney(change, { sign: true })} ({percent(pct, { sign: true })})</Delta>
          <span className="muted"> {t('forecast.from', { amount: compactMoney(view.current.netWorth) })}</span>
        </span>
      </section>
      <ModeToggle mode={mode} setMode={setMode} />
      <div className="card card--chart">
        <Chart view={view} flows={flows} mode={mode} visible={visible} height={272} compact />
      </div>
      {mode === 'cumulative' ? <SeriesChips visible={visible} onToggle={onToggle} wrap /> : <CashflowLegend />}
      <GoalsPanel view={view} compact />
      <AskAssistant view="forecast" />
      <ResultStamp result={result} />
    </div>
  );
}

function KeyNumbers({ view, result }: { view: ForecastView; result: ForecastResult }) {
  const m = view.forecast.metrics;
  const id = useId();
  const rows: { label: string; value: string; tone?: string }[] = [
    { label: t('forecast.key.monthlySavings'), value: money(num(m.monthlySavings)), tone: 'text-mint' },
    { label: t('forecast.key.savingsRate'), value: percent(num(m.savingsRate)) },
    { label: t('forecast.key.passive'), value: `${money(num(m.projectedPassiveIncome))}${t('common.perMonth')}`, tone: 'text-violet' },
    { label: t('debts.debtFree'), value: m.debtFreeDate ? monthYear(m.debtFreeDate) : t('common.none') },
    ...(typeof result.metrics.mortgage_free_date === 'string'
      ? [{ label: t('debts.mortgageFree'), value: monthYear(parseDay(result.metrics.mortgage_free_date)) }]
      : []),
    { label: t('forecast.key.totalIncome', { years: view.years }), value: compactMoney(num(m.totalIncome)) },
  ];
  return (
    <section className="card card--pad-lg" aria-labelledby={id}>
      <h2 className="eyebrow key-numbers__title" id={id}>{t('forecast.key.title')}</h2>
      <dl className="key-numbers">
        {rows.map((r) => (
          <div key={r.label} className="key-numbers__row">
            <dt>{r.label}</dt>
            <dd className={r.tone ?? ''}>{r.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

const GOAL_TONE: Record<string, 'cyber' | 'violet' | 'terracotta' | 'mint'> = {
  netWorth: 'cyber', investment: 'violet', debt: 'terracotta', cash: 'mint', passive: 'mint',
};

function GoalsPanel({ view, compact = false }: { view: ForecastView; compact?: boolean }) {
  const id = useId();
  return (
    <section className={compact ? 'stack-sm' : 'card card--pad-lg goals-panel'} aria-labelledby={id}>
      <div className="section-header">
        <h2 className="eyebrow" id={id}>{t('forecast.goals')}</h2>
        <Link to="/goals" className="text-link text-link--icon">
          <Icon name="plusCircle" size={14} strokeWidth={2.5} />
          {t('forecast.addGoal')}
        </Link>
      </div>
      {view.goals.length === 0 ? (
        <p className="muted">{t('goals.none')}</p>
      ) : (
        <ul className={compact ? 'goal-list goal-list--cards' : 'goal-list'}>
          {view.goals.map((g) => (
            <li key={g.goal.id} className="goal-list__item">
              <span className={`goal-list__icon goal-list__icon--${GOAL_TONE[g.goal.series] ?? 'cyber'}`} aria-hidden="true">
                <Icon name={g.goal.series === 'debt' ? 'card' : g.goal.series === 'investment' ? 'bars' : 'goals'} size={16} />
              </span>
              <span className="goal-list__text">
                <span className="goal-list__label">{g.goal.label}</span>
                <span className="goal-list__meta"><GoalStatus goal={g} trail={` · ${percent(g.progressPercent, { digits: 1 })}`} /></span>
              </span>
              <Ring percent={g.progressPercent} tone={GOAL_TONE[g.goal.series] ?? 'cyber'} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
